import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';
import dayjs from '~/timeInit';

import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { useCppInvoiceTransactions } from '~/hooks/apis/costPerPoint';

import type { CppInvoiceTransactionType, CppPartner } from '~/services/LuxLoyalty';

import { downloadCsvFile } from '~/utils/csvDownload';

import InvoiceNotesAlert from './InvoiceNotesAlert';
import InvoiceTotalsSummary from './InvoiceTotalsSummary';
import PartnerMonthFilters from './PartnerMonthFilters';
import { type PartnerTransactionRow } from './allPartnersTransactions';
import { describeCostSource, resolveCostStatus, tallyCostStatuses } from './costStatus';
import { formatCurrencyCents, formatSignedPoints } from './invoiceFormat';
import { type InvoiceMonth, formatMonthLabel, monthToRange } from './monthRange';
import { isAllPartners } from './partnerSelection';
import { formatTransactionCpp, transactionCsvHeaders, transactionsToCsvRows } from './transactionsCsv';
import { useAllPartnersTransactions } from './useAllPartnersTransactions';
import { useExportTransactionsCsv } from './useExportTransactionsCsv';

/**
 * Row classes used to tint the rows that carry no cost.
 *
 * A chip in the Cost column alone is easy to scroll past on a 50-row page, and an unpriced row is
 * the one thing here an admin has to act on — it blocks the invoice — so the whole row is marked.
 */
const UNPRICED_ROW_CLASS = 'cpp-row--unpriced';
const EXCLUDED_ROW_CLASS = 'cpp-row--excluded';

/** Page sizes offered by the grid. The endpoint caps a page at 500. */
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

/** Selectable transaction types, matching the endpoint's `transactionType` filter. */
const TRANSACTION_TYPES: Array<CppInvoiceTransactionType> = [
  'POINTS_CREDIT',
  'POINTS_DEBIT',
  'POINTS_REFUND',
  'TIER_UPGRADE',
];

/**
 * Renders a priced cost in the row's own currency.
 *
 * The currency code lives in its own column. A row with no currency is a bare amount, so it is not
 * presented as AUD.
 */
function formatPricedCost(amountCents: number, rowCurrency: string | null): string {
  const code = rowCurrency?.trim();
  return code ? formatCurrencyCents(amountCents, code) : (amountCents / 100).toFixed(2);
}

/** Maps a transaction type to a Chip colour. */
function typeColor(type: CppInvoiceTransactionType): 'success' | 'warning' | 'info' | 'default' {
  if (type === 'POINTS_CREDIT') {
    return 'success';
  }
  if (type === 'POINTS_DEBIT') {
    return 'warning';
  }
  if (type === 'POINTS_REFUND') {
    return 'info';
  }
  return 'default';
}

interface Props {
  partners: Array<CppPartner>;
  hasPartnersError: boolean;
  partnerId: string;
  month: InvoiceMonth;
  onPartnerChange: (partnerId: string) => void;
  onMonthChange: (month: InvoiceMonth) => void;
}

/**
 * Point issuance by partner at transaction level, for one calendar month.
 *
 * The grid is paginated by the service — a row's cost depends on the other rows landing on its day,
 * so neither the cost column nor the totals can be recomputed client-side. Export therefore walks
 * every page rather than dumping the visible one: the CSV is the activity statement that backs the
 * partner's invoice, so a partial file would be worse than none.
 */
export default function PartnerTransactionsPage({
  partners,
  hasPartnersError,
  partnerId,
  month,
  onPartnerChange,
  onMonthChange,
}: Props) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [transactionType, setTransactionType] = useState<CppInvoiceTransactionType | ''>('');

  const isAll = isAllPartners(partnerId);
  const range = useMemo(() => monthToRange(month), [month]);

  const query = useMemo(
    () => ({
      partnerId,
      ...range,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      sort_order: 'asc' as const,
      ...(transactionType && { transactionType }),
    }),
    [partnerId, range, paginationModel, transactionType],
  );

  const { data, isLoading, isFetching, isError, error } = useCppInvoiceTransactions(query, {
    enabled: Boolean(partnerId) && !isAll,
  });
  const allPartners = useAllPartnersTransactions({
    partners,
    range,
    transactionType,
    enabled: isAll && partners.length > 0,
  });

  const partnerName = useMemo(
    () => partners.find((partner) => partner.id === partnerId)?.name ?? '',
    [partners, partnerId],
  );

  const currency = isAll ? allPartners.currency : (data?.currency ?? null);
  const mixedCurrency = isAll && allPartners.mixedCurrency;
  const gridRows: Array<PartnerTransactionRow> = useMemo(() => {
    if (isAll) {
      return allPartners.rows;
    }
    return (data?.result ?? []).map((row) => ({ ...row, partnerName, currency: data?.currency ?? null }));
  }, [allPartners.rows, data?.currency, data?.result, isAll, partnerName]);
  const pageRows = useMemo(() => {
    if (!isAll) {
      return gridRows;
    }
    const start = paginationModel.page * paginationModel.pageSize;
    return gridRows.slice(start, start + paginationModel.pageSize);
  }, [gridRows, isAll, paginationModel.page, paginationModel.pageSize]);
  const costSourceCopy = useMemo(() => describeCostSource(pageRows), [pageRows]);
  const costStatusByTransaction = useMemo(
    () => new Map(gridRows.map((row) => [row.transactionId, resolveCostStatus(row)])),
    [gridRows],
  );
  const { exportCsv, cancel: cancelExport, progress: exportProgress, isExporting } = useExportTransactionsCsv();
  const costTally = useMemo(() => tallyCostStatuses(pageRows), [pageRows]);

  // An export belongs to the selection that started it, so moving off that selection abandons the
  // walk instead of leaving it fetching pages for a period no longer on screen.
  useEffect(() => cancelExport, [cancelExport, partnerId, month, transactionType]);

  useEffect(() => {
    setPaginationModel((previous) => (previous.page === 0 ? previous : { ...previous, page: 0 }));
  }, [partnerId, month, transactionType]);

  const handleExport = useCallback(() => {
    if (!isAll) {
      exportCsv({ partnerId, partnerName, range, transactionType });
      return;
    }

    if (gridRows.length === 0) {
      return;
    }

    const csvOptions = { perRowCurrency: mixedCurrency };
    downloadCsvFile(
      transactionsToCsvRows(gridRows, 'All partners', currency, csvOptions),
      transactionCsvHeaders(currency, csvOptions),
      `all_partners_transactions_${range.start}_${range.end}.csv`,
    );
  }, [currency, exportCsv, gridRows, isAll, mixedCurrency, partnerId, partnerName, range, transactionType]);

  const columns: Array<GridColDef<PartnerTransactionRow>> = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        display: 'flex',
        width: 120,
        // The service's own Sydney day, not the browser's rendering of `createdAt`. Those disagree
        // for a row near midnight, and this column has to match the day the row is priced against.
        valueFormatter: (value: string) => dayjs(value).format('DD MMM YYYY'),
      },
      {
        field: 'createdAt',
        headerName: 'Time',
        display: 'flex',
        width: 100,
        // Shown in the program's timezone so it agrees with the Date beside it whatever the
        // browser's locale. Sorting stays correct: the raw value is an ISO instant.
        valueFormatter: (value: string) => dayjs(value).tz().format('HH:mm:ss'),
      },
      {
        field: 'partnerName',
        headerName: 'Partner',
        display: 'flex',
        width: 140,
        valueGetter: (_value, row) => row.partnerName,
        sortable: isAll,
      },
      {
        field: 'transactionType',
        headerName: 'Type',
        display: 'flex',
        width: 150,
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" color={typeColor(row.transactionType)} label={row.transactionType} />
        ),
      },
      {
        field: 'activityTitle',
        headerName: 'Activity',
        display: 'flex',
        flex: 1,
        minWidth: 200,
        // Kept to one line and ellipsised, with the full title on hover: activity titles are
        // free text from the partner and are long enough to wrap out of the row otherwise.
        renderCell: ({ row }) => (
          <Tooltip title={row.activityTitle}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0, width: '100%' }}>
              {row.activityTitle}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'accountNumber',
        headerName: 'Account',
        display: 'flex',
        width: 120,
        renderCell: ({ row }) => {
          if (!row.customerId) {
            return row.accountNumber;
          }

          return (
            <Link
              component={RouterLink}
              to={`/users/${row.customerId}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              onClick={(event) => event.stopPropagation()}
            >
              {row.accountNumber}
            </Link>
          );
        },
      },
      {
        field: 'signedPoints',
        headerName: 'Points',
        display: 'flex',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => formatSignedPoints(row.signedPoints),
      },
      {
        field: 'signedBonusPoints',
        headerName: 'Bonus Points',
        display: 'flex',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => formatSignedPoints(row.signedBonusPoints),
      },
      {
        field: 'cpp',
        headerName: 'CPP',
        display: 'flex',
        width: 90,
        sortable: false,
        valueGetter: (_value, row) => formatTransactionCpp(row),
      },
      {
        field: 'currency',
        headerName: 'Currency',
        display: 'flex',
        width: 110,
        valueGetter: (_value, row) => row.currency?.trim() || '',
      },
      {
        field: 'amountCents',
        headerName: 'Cost',
        display: 'flex',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => {
          const status = costStatusByTransaction.get(row.transactionId) ?? resolveCostStatus(row);

          // An excluded row is the invoice deliberately not billing for something; an unpriced one
          // is a data problem that blocks the invoice. They must not look the same.
          if (status.kind === 'PRICED') {
            return formatPricedCost(row.amountCents ?? 0, row.currency);
          }

          return (
            <Tooltip title={status.tooltip ?? ''}>
              <Chip size="small" color={status.severity} variant="outlined" label={status.label} />
            </Tooltip>
          );
        },
      },
    ],
    [costStatusByTransaction, isAll],
  );

  return (
    <Stack spacing={3}>
      <PartnerMonthFilters
        partners={partners}
        partnerId={partnerId}
        month={month}
        onPartnerChange={onPartnerChange}
        onMonthChange={onMonthChange}
        hasPartnersError={hasPartnersError}
        allowAllPartners
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={
              isExporting ||
              (isAll
                ? allPartners.isLoading || gridRows.length === 0
                : !partnerId || !data || data.pagination.total_items === 0)
            }
          >
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      <TextField
        select
        size="small"
        label="Transaction type"
        value={transactionType}
        onChange={(event) => {
          setTransactionType(event.target.value as CppInvoiceTransactionType | '');
          setPaginationModel((previous) => ({ ...previous, page: 0 }));
        }}
        sx={{ minWidth: 220 }}
      >
        <MenuItem value="">All types</MenuItem>
        {TRANSACTION_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>

      {exportProgress && <Alert severity="info">{exportProgress}</Alert>}

      {isError && !isAll && (
        <Alert severity="error">
          Could not load transactions: {error instanceof Error ? error.message : 'unknown error'}
        </Alert>
      )}

      {isAll && allPartners.failedPartners.length > 0 && (
        <Alert severity="error">
          Could not load transactions for {allPartners.failedPartners.map((partner) => partner.name).join(', ')}.
        </Alert>
      )}

      {!partnerId && <Alert severity="info">Select a partner, or all partners, to view point issuance.</Alert>}

      {isAll && allPartners.isFetching && (
        <Alert severity="info">
          Loading transactions for {allPartners.loadedCount} of {allPartners.partnerCount} partners…
        </Alert>
      )}

      {!isAll && partnerId && data && (
        <>
          <InvoiceNotesAlert
            notes={data.blockers}
            severity="error"
            title={`This period cannot be invoiced as it stands — ${formatMonthLabel(month)}`}
          />
          <InvoiceTotalsSummary totals={data.totals} currency={currency} />
          {costSourceCopy && (
            <Typography variant="caption" color="text.secondary">
              {costSourceCopy}
            </Typography>
          )}
        </>
      )}

      {isAll && allPartners.isSettled && allPartners.totals && (
        <>
          {allPartners.blockedPartnerNames.length > 0 && (
            <Alert severity="error">
              This period cannot be invoiced for {allPartners.blockedPartnerNames.join(', ')}. Open a single partner to
              see what is blocking it.
            </Alert>
          )}
          {allPartners.mixedCurrency && (
            <Alert severity="warning">
              These partners are priced in more than one currency, so the costs are not combined. Each row shows its own
              currency.
            </Alert>
          )}
          {allPartners.truncated && (
            <Alert severity="warning">
              Some partners have more transactions than could be loaded. The totals still cover each partner&apos;s
              whole period.
            </Alert>
          )}
          <InvoiceTotalsSummary totals={allPartners.totals} currency={currency} omitCost={allPartners.mixedCurrency} />
          {costSourceCopy && (
            <Typography variant="caption" color="text.secondary">
              {costSourceCopy}
            </Typography>
          )}
        </>
      )}

      {(isAll || partnerId) && costTally.unpriced > 0 && (
        <Alert severity="error">
          <AlertTitle>
            {costTally.unpriced === 1
              ? '1 transaction on this page could not be priced'
              : `${costTally.unpriced} transactions on this page could not be priced`}
          </AlertTitle>
          No rate card covered the day {costTally.unpriced === 1 ? 'it is' : 'they are'} priced against, so{' '}
          {costTally.unpriced === 1 ? 'it carries' : 'they carry'} no cost and the invoice is blocked until a card is in
          place. The affected {costTally.unpriced === 1 ? 'row is' : 'rows are'} highlighted below.
        </Alert>
      )}

      {(isAll || partnerId) && costTally.excluded > 0 && (
        <Alert severity="info">
          {costTally.excluded === 1
            ? '1 transaction on this page is excluded from billing: it is a refund reversing activity priced on a tiered rate card. This is deliberate and does not block the invoice.'
            : `${costTally.excluded} transactions on this page are excluded from billing: they are refunds reversing activity priced on a tiered rate card. This is deliberate and does not block the invoice.`}
        </Alert>
      )}

      <Box>
        <DataGridPro
          autoHeight
          disableRowSelectionOnClick
          getRowId={(row: PartnerTransactionRow) => row.transactionId}
          rows={partnerId ? gridRows : []}
          columns={columns}
          loading={isAll ? allPartners.isLoading || allPartners.isFetching : isLoading || isFetching}
          paginationMode={isAll ? 'client' : 'server'}
          rowCount={isAll ? gridRows.length : (data?.pagination.total_items ?? 0)}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pagination
          getRowClassName={({ row }: { row: PartnerTransactionRow }) => {
            const { kind } = costStatusByTransaction.get(row.transactionId) ?? resolveCostStatus(row);

            if (kind === 'UNPRICED') {
              return UNPRICED_ROW_CLASS;
            }
            return kind === 'EXCLUDED' ? EXCLUDED_ROW_CLASS : '';
          }}
          // Tints are derived from the palette so they track the theme, and are kept faint enough
          // that the row's own text stays legible in both light and dark mode. Selectors carry two
          // classes so they win against the grid's own `.MuiDataGrid-row:hover` rule.
          sx={{
            // Custom cells (the activity title, the type chip) sit in a flex row so they line up
            // with the plain text columns instead of resting on the top of the cell.
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end' },
            [`& .MuiDataGrid-row.${UNPRICED_ROW_CLASS}`]: {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.16) },
            },
            [`& .MuiDataGrid-row.${EXCLUDED_ROW_CLASS}`]: {
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.07),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.info.main, 0.13) },
            },
          }}
        />
      </Box>
    </Stack>
  );
}
